<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { page } from '$stores/pageStore';
  const browser = typeof window !== 'undefined';
  import PopupForm from './sites/SiteForm.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import { isCompactMode } from '$stores/uiStore';
  import Pagination from '$lib/Pagination.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '../lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronLeft,
    faChevronRight,
  faChevronDown,
    faSquarePlus,
    faTrashCan,
    faPencil,
    faCircleDot,
    faRotate,
    faSearch,
    faXmark,
    faCircleXmark,
    faHistory,
    faEllipsis,
    faFileArrowUp
  } from '@fortawesome/pro-light-svg-icons';
  import { user, userRoles, userReadOnly } from '$stores/sessionStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';
  import SitesInfoCards from '$lib/SitesInfoCards.svelte';
  import StudyForm from './studies/StudyForm.svelte';
  import StudyPropertiesEditor from './studies/StudyPropertiesEditor.svelte';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
import * as XLSX from 'xlsx';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';
const EXCEL_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.xlsb'];
const REQUIRED_SITE_HEADER_KEYS = ['siteid', 'sitename', 'country', 'timezone'];
const MAX_IMPORT_ERRORS_TO_DISPLAY = 25;
const SITE_IMPORT_TEMPLATE_COLUMNS = [
  'Site ID',
  'Site Name',
  'Country',
  'Timezone',
  'City',
  'State',
  'Postal Code',
  'Address 1',
  'Address 2',
  'Primary Investigator',
  'Emails',
  'Phone Number',
  'Notes',
  'Status',
  'Active',
  'Enrollment Target',
  'Enrollment Start Date',
  'Enrollment End Date',
  'External ID',
  'Description'
];
const COLUMN_TO_FIELD_MAP = {
  siteid: 'siteId',
  sitename: 'siteName',
  description: 'description',
  address1: 'address1',
  address2: 'address2',
  city: 'city',
  state: 'state',
  province: 'state',
  postalcode: 'postalCode',
  zipcode: 'postalCode',
  zip: 'postalCode',
  country: 'country',
  countryguid: 'countryGUID',
  timezone: 'timezone',
  timezoneguid: 'timezoneGUID',
  primaryinvestigator: 'primaryInvestigator',
  investigator: 'primaryInvestigator',
  emails: 'emails',
  email: 'emails',
  phonenumber: 'phoneNumber',
  phone: 'phoneNumber',
  notes: 'notes',
  externalid: 'externalId',
  enrollmenttarget: 'enrollmentTarget',
  target: 'enrollmentTarget',
  enrollmentstartdate: 'enrollmentStartDate',
  startdate: 'enrollmentStartDate',
  enrollmentenddate: 'enrollmentEndDate',
  enddate: 'enrollmentEndDate',
  active: 'active',
  status: 'statusLabel',
  statuslabel: 'statusLabel',
  statusguid: 'statusGUID'
};

  let sites = [];
  let allSites = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let searchText = '';
  let isExporting = false;

  let show = false;
  let showForm = false;
  let showConfirmation = false;
  let showAuditLog = false;
  let editSite = null;
  let siteToDelete = null;
  let auditLogSite = null;
  let studyId = null;
  let isInitialized = false;

  let showStudyForm = false;
  let showStudyPropertiesEditor = false;
  let editStudy = null;
  let studyFormError = '';
  let auditLogStudy = null;
  let showStudyAuditLog = false;

  let showMenu = false;
  let menuSite = null;
  let menuPosition = { top: 0, left: 0, direction: 'down' };
let showCreateMenu = false;
let importFileInput;
let isImportingSites = false;
let importStatusMessage = '';
let importProgress = null;
let importSummary = null;
let lookupPromise = null;
let lookupCache = { statuses: [], countries: [], timezones: [] };
let defaultStatusGuid = null;
let showImportModal = false;

  // Feature flag for sites card view
  let isSitesCardViewEnabled = true;

  // Filter for participant count
  let participantFilter = 'all'; // 'all' or 'withParticipants'

  // Reactive variable for feature flags
  $: {
    isSitesCardViewEnabled = isFeatureEnabled('cardViews.sites');
  }

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else {
    studyProperties.set(null);
  }

  onMount(async () => {
    // console.log('Sites page mounted, selectedStudy:', $selectedStudy);
    
    //selectedStudy.set(null);
    selectedSite.set(null);
    selectedParticipant.set(null);

    // Get search text from URL parameter if it exists
    const urlSearchText = $page.url.searchParams.get('search');
    if (urlSearchText) {
      searchText = urlSearchText;
    }

    // Get page number from URL parameter if it exists
    const urlPage = parseInt($page.url.searchParams.get('page')) || 1;
    currentPage = urlPage;

    // Get filter from URL parameter if it exists
    const urlFilter = $page.url.searchParams.get('filter');
    if (urlFilter && ['all', 'withParticipants'].includes(urlFilter)) {
      participantFilter = urlFilter;
    }

    // If selectedStudy is not available, try to retrieve it from localStorage
    if (!$selectedStudy && browser) {
      const storedStudy = localStorage.getItem('selectedStudy');
      if (storedStudy) {
        try {
          const parsedStudy = JSON.parse(storedStudy);
          // console.log('Retrieved selectedStudy from localStorage in onMount:', parsedStudy);
          selectedStudy.set(parsedStudy);
        } catch (error) {
          console.error('Error parsing selectedStudy from localStorage in onMount:', error);
        }
      }
    }

    // If still no selectedStudy, retry a few times with a delay
    // This helps in production where the navigation might be faster than store updates
    let attempts = 0;
    const maxAttempts = 3;
    
    const tryFetchSites = async () => {
      if ($selectedStudy) {
        // console.log('selectedStudy is available, fetching sites');
        fetchSites(currentPage);
      } else if (attempts < maxAttempts) {
        attempts++;
        // console.log(`No selectedStudy yet, retrying in 300ms (attempt ${attempts}/${maxAttempts})`);
        
        // Try to get it from localStorage one more time
        if (browser) {
          const storedStudy = localStorage.getItem('selectedStudy');
          if (storedStudy) {
            try {
              const parsedStudy = JSON.parse(storedStudy);
              // console.log(`Retry ${attempts}: Retrieved selectedStudy from localStorage:`, parsedStudy);
              selectedStudy.set(parsedStudy);
              
              // If we got the study, fetch sites immediately
              if (parsedStudy && parsedStudy._id) {
                fetchSites(currentPage);
                return;
              }
            } catch (error) {
              console.error(`Retry ${attempts}: Error parsing selectedStudy:`, error);
            }
          }
        }
        
        // If still no success, retry after a delay
        setTimeout(tryFetchSites, 300);
      } else {
        console.warn('No selectedStudy available after multiple attempts');
      }
    };
    
    tryFetchSites();
    
    // Mark as initialized after initial setup
    isInitialized = true;
  });

  async function fetchSites(page = 1) {
    try {
      isLoading = true;
      // console.log('fetchSites - selectedStudy:', $selectedStudy);
      
      // If we don't have a selectedStudy, try to get it from localStorage directly
      if (!$selectedStudy) {
        const storedStudy = browser && localStorage.getItem('selectedStudy');
        if (storedStudy) {
          try {
            const parsedStudy = JSON.parse(storedStudy);
            // console.log('Retrieved selectedStudy from localStorage:', parsedStudy);
            if (parsedStudy && parsedStudy._id) {
              // Update the store
              selectedStudy.set(parsedStudy);
            } else {
              console.error('Invalid or incomplete study data in localStorage');
              return;
            }
          } catch (error) {
            console.error('Error parsing selectedStudy from localStorage:', error);
            return;
          }
        } else {
          console.error('No selectedStudy available and none in localStorage');
          return;
        }
      }
      
      // Ensure we have a valid selectedStudy._id before making the API call
      if (!$selectedStudy || !$selectedStudy._id) {
        console.error('Missing selectedStudy or selectedStudy._id, cannot fetch sites');
        return;
      }
      
      // The API expects studyGUID to be a valid MongoDB ObjectId
      // Some versions of MongoDB require an exact 24-character hex string
      // Ensure studyGUID is properly formatted
      const studyId = $selectedStudy._id;
      
      // First try the regular sites endpoint with query parameters
      const requestUrl = `${API_BASE_URL}/sites?page=${page}&pageSize=${limit}&searchText=${searchText}&sortOrder=asc&studyGUID=${studyId}&participantFilter=${participantFilter}`;
      // console.log('API Request URL:', requestUrl);
      
      const res = await fetch(requestUrl);

      if (!res.ok) {
        throw new Error('Failed to fetch sites');
      }

      const responseData = await res.json();
      // console.log('API Response:', responseData);

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      if (demoMode) {
        sites = responseData.data.map((site) => ({
          ...site,
          siteId: `${site.siteId}`,
          siteName: `Demo Site ${site.siteId}`,
        }));
      } else {
        sites = responseData.data;
      }

      // If we received all sites instead of only the ones for the selected study,
      // fallback to the study-specific endpoint
      if (sites.length > 0 && !sites.every(site => 
          site.studyGUID === studyId || 
          site.studyGUID?._id === studyId || 
          site.studyId === $selectedStudy.studyId)) {
        
        // console.log('Sites not properly filtered by study, trying alternative endpoint');
        const alternativeSucceeded = await tryAlternativeEndpoint();
        
        if (!alternativeSucceeded) {
          console.error('Both endpoints failed to filter sites by study');
        }
      } 
    } catch (err) {
      console.error('Error fetching sites:', err);
      
      // Try the alternative endpoint as a fallback
      console.log('Error with main endpoint, trying alternative endpoint');
      await tryAlternativeEndpoint();
      
      sites = sites || [];
    } finally {
      isLoading = false;
    }
  }

  async function handleCreateSite(event) {
    const newSite = event.detail;
    //console.log('newSite: ', JSON.stringify(newSite));
    try {
      const res = await fetch(`${API_BASE_URL}/sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSite),
      });

      if (!res.ok) {
        let msg = `Failed to create site (${res.status})`;
        try { const e = await res.json(); msg = e.message || e.error || msg; } catch (_) { /* non-JSON */ }
        throw new Error(msg);
      }

      await fetchSites();
      showForm = false;
    } catch (err) {
      // Previously this only console.error'd, so a failed Save looked like
      // nothing happened (SPTL-738). Surface it so the user gets feedback.
      console.error('Error creating site:', err);
      alert(`Could not create the site: ${err.message}`);
    }
  }

  async function handleEditSite(event) {
    const updatedSite = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/sites/${updatedSite.siteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedSite),
      });

      if (!res.ok) {
        throw new Error('Failed to update site');
      }

      // Refresh $selectedSite from the enriched save response so the site
      // detail header (Local Time, Timezone label, etc.) re-formats with the
      // new values instead of holding the stale pre-edit snapshot.
      const responseBody = await res.json();
      if (responseBody?.data && $selectedSite && $selectedSite._id === responseBody.data._id) {
        selectedSite.set(responseBody.data);
      }

      await fetchSites();
      showForm = false;
      editSite = null;
    } catch (err) {
      console.error('Error updating site:', err);
    }
  }

  async function confirmDelete() {
    if (siteToDelete) {
      try {
        const res = await fetch(`${API_BASE_URL}/sites/${siteToDelete._id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
        });

        if (!res.ok) {
          throw new Error('Failed to delete site');
        }

        await fetchSites();
        showConfirmation = false;
        siteToDelete = null;
      } catch (err) {
        console.error('Error deleting site:', err);
      }
    }
  }

  function handleDeleteClick(site) {
    siteToDelete = site;
    showConfirmation = true;
  }

  function handleAuditLogClick(site) {
    auditLogSite = site;
    showAuditLog = true;
  }

  function handleSiteClick(site) {
    selectedSite.set(site);
    selectedParticipant.set(null);
    goto(`/persons`);
    //goto(`/persons?siteId=${site.siteId}`);
  }

  function handleRefresh() {
    fetchSites();
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      // Update URL with page number
      const url = new URL(window.location);
      url.searchParams.set('page', newPage.toString());
      goto(url.toString(), { replaceState: true });
      
      fetchSites(newPage);
    }
  }

  function handleEditClick(site) {
    editSite = site;
    showForm = true;
  }

  function handleNewClick() {
    studyId = $selectedStudy ? $selectedStudy.studyId : 'NONE';
    editSite = null;
    showForm = true;
  }

  function handleSearch() {
    // Update URL with search text and reset page to 1
    const url = new URL(window.location);
    if (searchText) {
      url.searchParams.set('search', searchText);
    } else {
      url.searchParams.delete('search');
    }
    url.searchParams.set('page', '1'); // Reset to page 1 when searching
    goto(url.toString(), { replaceState: true });
    fetchSites(1);
  }

  function handleClearSearch() {
    searchText = '';
    // Reset page to 1 when clearing search
    currentPage = 1;
    handleSearch();
  }

  function handleFilterChange() {
    // Reset page to 1 when changing filter
    currentPage = 1;
    
    // Update URL with filter parameter
    const url = new URL(window.location);
    if (participantFilter !== 'all') {
      url.searchParams.set('filter', participantFilter);
    } else {
      url.searchParams.delete('filter');
    }
    url.searchParams.set('page', '1'); // Reset to page 1 when changing filter
    goto(url.toString(), { replaceState: true });
    
    fetchSites(1);
  }

  // Add computed property for edit permissions using the centralized hasRole function
  $: canAddSites = hasAccessUiAction('sites.add', $userRoles, $studyProperties, $userReadOnly);
  $: canEditSites = hasAccessUiAction('sites.edit', $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteSites = hasAccessUiAction('sites.delete', $userRoles, $studyProperties, $userReadOnly);
  $: canViewAuditLogs = hasAccessUiAction(
    'sites.details.viewAuditLogs',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  // Debug mode can be enabled with a URL parameter
  $: debugMode = false; // browser && ($page.url.searchParams.get('debug') === 'true' || import.meta.env.DEV);
  
  // Track the study ID from localStorage and the store for debugging
  let storedStudyFromLocalStorage = '';
  $: if (browser && debugMode) {
    try {
      const raw = localStorage.getItem('selectedStudy');
      if (raw) {
        const parsed = JSON.parse(raw);
        storedStudyFromLocalStorage = parsed ? 
          `${parsed.studyName} (${parsed._id})` : 
          'Invalid Study Data';
      } else {
        storedStudyFromLocalStorage = 'Not Found in localStorage';
      }
    } catch (e) {
      storedStudyFromLocalStorage = `Error: ${e.message}`;
    }
  }
  
  // Add a debug string based on selectedStudy
  $: studyDebugInfo = $selectedStudy ? 
     `Study from store: ${$selectedStudy.studyName} (${$selectedStudy._id})` : 
     'No Study in store';

  // Debug function to directly query the API
  async function testDirectApiCall() {
    if (!$selectedStudy || !$selectedStudy._id) {
      // console.error('No study selected');
      return;
    }
    
    try {
      // Try several variations of the study parameter
      const testVariations = [
        `studyGUID=${$selectedStudy._id}`,
        `studyID=${$selectedStudy.studyId}`,
        `studyId=${$selectedStudy.studyId}`,
        `study=${$selectedStudy._id}`
      ];
      
      for (const param of testVariations) {
        const url = `${API_BASE_URL}/sites?${param}`;
        // console.log(`Testing API with: ${url}`);
        
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          // console.log(`Response for ${param}:`, data);
          // console.log(`Got ${data.data?.length || 0} sites`);
        } else {
          console.log(`Failed call with ${param}: ${res.status}`);
        }
      }
    } catch (err) {
      console.error('Error in direct API test:', err);
    }
  }

  // Try alternative endpoint for study filtering
  async function tryAlternativeEndpoint() {
    if (!$selectedStudy || !$selectedStudy._id) {
      console.error('No study selected');
      return false;
    }
    
    try {
      // The API has a dedicated route for filtering by study
      const alternativeUrl = `${API_BASE_URL}/sites/study/${$selectedStudy._id}`;
      // console.log(`Trying alternative study-specific endpoint: ${alternativeUrl}`);
      
      const res = await fetch(alternativeUrl);
      if (res.ok) {
        const data = await res.json();
        // console.log('Alternative endpoint response:', data);
        
        if (Array.isArray(data)) {
          // Update the sites with the filtered results
          sites = data;
          resultCount = data.length;
          // console.log(`Got ${data.length} sites from alternative endpoint`);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Error with alternative endpoint:', err);
      return false;
    }
  }

  // Add a store subscription, but only in the browser
  $: if (browser && isInitialized && $selectedStudy) {
    fetchSites(currentPage).catch(err => {
      console.error('Error in store subscription:', err);
      hasError = true;
      errorMessage = err.message;
    });
    fetchAllSites().catch(err => {
      console.error('Error fetching all sites:', err);
    });
  }

  // React to filter changes
  $: if (browser && isInitialized && $selectedStudy && participantFilter) {
    fetchAllSites().catch(err => {
      console.error('Error fetching all sites after filter change:', err);
    });
  }

  // Utility function to format location based on country
  function formatLocation(site) {
    const { city, state, country } = site;
    
    // If no country, just return city and state as is
    if (!country) {
      return [city, state].filter(Boolean).join(', ');
    }
    
    // Handle different country formats
    const countryLower = country.toLowerCase();
    
    // United States format: City, State
    if (countryLower.includes('united states') || countryLower === 'us' || countryLower === 'usa') {
      if (city && state) {
        return `${city}, ${state}`;
      } else if (city) {
        return city;
      } else if (state) {
        return state;
      }
      return '';
    }
    
    // Canada format: City, Province
    if (countryLower === 'canada' || countryLower === 'ca') {
      if (city && state) {
        return `${city}, ${state}`;
      } else if (city) {
        return city;
      } else if (state) {
        return state;
      }
      return '';
    }
    
    // Most other countries: City, State/Province/Region
    if (city && state) {
      return `${city}, ${state}`;
    } else if (city) {
      return city;
    } else if (state) {
      return state;
    }
    
    return '';
  }

  async function fetchAllSites() {
    if (!$selectedStudy || !$selectedStudy._id) {
      allSites = [];
      return;
    }

    try {
      const url = `${API_BASE_URL}/sites?page=1&pageSize=999999&searchText=${searchText}&sortOrder=asc&studyGUID=${$selectedStudy._id}&participantFilter=${participantFilter}`;
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all sites');
      }

      const responseData = await res.json();
      allSites = responseData.data || [];
    } catch (err) {
      console.error('Error fetching all sites:', err);
      allSites = [];
    }
  }

  async function handleEditStudy(event) {
    const updatedStudy = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/studies/${updatedStudy._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedStudy),
      });

      if (!res.ok) {
        // Surface the server's own message. The uniqueness pre-check fails open
        // now, so a genuine duplicate is caught here — swallowing this would
        // make Save appear to do nothing at all.
        const errorBody = await res.json().catch(() => ({}));
        studyFormError = errorBody?.message || errorBody?.error
          || `Failed to update study (${res.status})`;
        return;
      }

      // Use the enriched save response (has statusCode/statusLabel from the
      // backend aggregation), not the raw form data which only has IDs.
      // Otherwise study-header derived fields stay stale post-edit.
      const responseBody = await res.json();
      selectedStudy.set(responseBody?.data ?? updatedStudy);
      studyFormError = '';
      showStudyForm = false;
      editStudy = null;
    } catch (err) {
      console.error('Error updating study:', err);
      studyFormError = 'Could not reach the server. Please try again.';
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      if (!$selectedStudy || !$selectedStudy._id) {
        throw new Error('No study selected');
      }

      const url = `${API_BASE_URL}/sites?page=1&pageSize=999999&searchText=${searchText}&sortOrder=asc&studyGUID=${$selectedStudy._id}&participantFilter=${participantFilter}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch sites data for export');
      }
      const responseData = await res.json();
      const exportSites = responseData.data || [];

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'Sites'
      });

      const formattedData = exportSites.map((site) => ({
        Protocol: $selectedStudy?.protocolNumber || '',
        Site: site.siteId || '',
        Name: site.siteName || '',
        Location: formatLocation(site),
        Country: (site.flag ? site.flag + ' ' : '') + (site.country ? site.country : ''),
        ParticipantsTotal: site.subjectCount ?? 0,
        Status: site.statusLabel || ''
      }));

      //ParticipantsActive: site.activeSubjectCount ?? 0,

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting sites to Excel:', error);
    } finally {
      isExporting = false;
    }
  }

  function handleMenuClick(site, event) {
    event.stopPropagation();
    menuSite = site;
    // Get button position
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 150; // Approximate, adjust as needed
    const menuWidth = 192; // w-48 in px
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    let direction = 'down';
    let top = buttonRect.bottom + window.scrollY;
    let left = buttonRect.left + window.scrollX;
    if (buttonRect.bottom + menuHeight > viewportHeight) {
      direction = 'up';
      top = buttonRect.top + window.scrollY - menuHeight;
      if (top < 0) top = 0;
    }
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
      if (left < 0) left = 0;
    }
    menuPosition = { top, left, direction };
    showMenu = true;
  }

  function handleMenuClose() {
    showMenu = false;
    menuSite = null;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
  if (showCreateMenu && !event.target.closest('.create-site-menu')) {
    showCreateMenu = false;
  }
  }

function handleCreateMenuToggle(event) {
  event?.stopPropagation();
  if (!canAddSites) {
    return;
  }
  showCreateMenu = !showCreateMenu;
}

function handleCreateMenuFormOption() {
  showCreateMenu = false;
  handleNewClick();
}

function handleCreateMenuExcelOption() {
  showCreateMenu = false;
  importSummary = null;
  if (!importFileInput) {
    return;
  }
  importFileInput.value = '';
  importFileInput.click();
}

function handleDownloadSiteTemplate() {
  try {
    const worksheetData = [
      SITE_IMPORT_TEMPLATE_COLUMNS,
      [
        'SITE-001',
        'Example Site',
        'United States',
        'America/New_York',
        'Boston',
        'MA',
        '02108',
        '1 Beacon St',
        '',
        'Dr. Smith',
        'site@example.com',
        '+1 555 555 1212',
        '',
        'Active',
        'Yes',
        100,
        '2025-01-01',
        '2025-12-31',
        'EXT-001',
        'Optional description'
      ]
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sites');
    const filenameBase = generateExportFilename({
      protocolNumber: $selectedStudy?.protocolNumber,
      reportType: 'Site Import Template'
    });
    const filename = filenameBase ? `${filenameBase}.xlsx` : 'site-import-template.xlsx';
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Failed to download template:', error);
  }
}

async function handleImportFileChange(event) {
  const file = event?.target?.files?.[0];
  if (!file) {
    return;
  }
  await processSiteExcelFile(file);
  event.target.value = '';
}

function closeImportModal() {
  if (isImportingSites) {
    return;
  }
  showImportModal = false;
  importSummary = null;
  importProgress = null;
  importStatusMessage = '';
}

async function ensureLookupData() {
  if (lookupCache.countries.length && lookupCache.timezones.length && lookupCache.statuses.length) {
    if (!defaultStatusGuid) {
      const activeStatus =
        lookupCache.statuses.find(
          (status) => normalizeComparisonValue(status?.label) === 'active' || normalizeComparisonValue(status?.code) === 'active'
        ) || lookupCache.statuses[0];
      defaultStatusGuid = activeStatus?.id || null;
    }
    return;
  }
  if (!lookupPromise) {
    lookupPromise = Promise.all([
      fetch(`${API_BASE_URL}/lookups/STAT`),
      fetch(`${API_BASE_URL}/lookups/COUNTRY`),
      fetch(`${API_BASE_URL}/lookups/TIMEZONE`)
    ])
      .then(async ([statusRes, countryRes, timezoneRes]) => {
        if (!statusRes.ok || !countryRes.ok || !timezoneRes.ok) {
          throw new Error('Failed to load lookup data');
        }
        const [statusData, countryData, timezoneData] = await Promise.all([
          statusRes.json(),
          countryRes.json(),
          timezoneRes.json()
        ]);
        lookupCache = {
          statuses: statusData?.data || [],
          countries: countryData?.data || [],
          timezones: timezoneData?.data || []
        };
        const activeStatus =
          lookupCache.statuses.find(
            (status) => normalizeComparisonValue(status?.label) === 'active' || normalizeComparisonValue(status?.code) === 'active'
          ) || lookupCache.statuses[0];
        defaultStatusGuid = activeStatus?.id || null;
      })
      .finally(() => {
        lookupPromise = null;
      });
  }
  await lookupPromise;
}

function normalizeColumnKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function normalizeComparisonValue(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_\-()]/g, '')
    .replace(/\//g, '');
}

function toTrimmedString(value) {
  if (value === null || value === undefined) {
    return '';
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value).trim();
}

function parseBooleanValue(value, fallback = true) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  const normalized = normalizeComparisonValue(value);
  if (['yes', 'true', '1', 'active'].includes(normalized)) {
    return true;
  }
  if (['no', 'false', '0', 'inactive'].includes(normalized)) {
    return false;
  }
  return fallback;
}

function normalizeDateValue(value) {
  if (!value) {
    return '';
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString();
  }
  const cleaned = toTrimmedString(value);
  if (!cleaned) {
    return '';
  }
  const parsed = new Date(cleaned);
  if (isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString();
}

function mapExcelRow(row, index) {
  const mapped = { __rowNumber: index + 2 };
  let hasValue = false;
  Object.entries(row || {}).forEach(([key, rawValue]) => {
    const normalizedKey = normalizeColumnKey(key);
    const mappedField = COLUMN_TO_FIELD_MAP[normalizedKey];
    if (!mappedField) {
      return;
    }
    let value = rawValue;
    if (typeof value === 'string') {
      value = value.trim();
    }
    if (value !== null && value !== undefined && !(typeof value === 'string' && value === '')) {
      hasValue = true;
    }
    mapped[mappedField] = value;
  });
  return hasValue ? mapped : null;
}

function findCountryGuid(value) {
  if (!value) {
    return null;
  }
  const normalized = normalizeComparisonValue(value);
  if (!normalized) {
    return null;
  }
  const match = lookupCache.countries.find(
    (country) =>
      normalizeComparisonValue(country?.label) === normalized ||
      normalizeComparisonValue(country?.code) === normalized ||
      normalizeComparisonValue(country?.name) === normalized
  );
  return match?.id || null;
}

function findTimezoneGuid(value) {
  if (!value) {
    return null;
  }
  const cleaned = String(value).split('(')[0];
  const normalized = normalizeComparisonValue(cleaned);
  if (!normalized) {
    return null;
  }
  const match = lookupCache.timezones.find(
    (timezone) =>
      normalizeComparisonValue(timezone?.code) === normalized ||
      normalizeComparisonValue(timezone?.label) === normalized ||
      normalizeComparisonValue(timezone?.name) === normalized
  );
  return match?.id || null;
}

function findStatusGuid(value) {
  if (!value) {
    return null;
  }
  const normalized = normalizeComparisonValue(value);
  if (!normalized) {
    return null;
  }
  const match = lookupCache.statuses.find(
    (status) =>
      normalizeComparisonValue(status?.label) === normalized ||
      normalizeComparisonValue(status?.code) === normalized
  );
  return match?.id || null;
}

function buildSitePayload(row) {
  const errors = [];
  const payload = {
    studyGUID: $selectedStudy?._id || '',
    siteId: toTrimmedString(row?.siteId),
    siteName: toTrimmedString(row?.siteName),
    description: toTrimmedString(row?.description),
    address1: toTrimmedString(row?.address1),
    address2: toTrimmedString(row?.address2),
    city: toTrimmedString(row?.city),
    state: toTrimmedString(row?.state),
    postalCode: toTrimmedString(row?.postalCode),
    primaryInvestigator: toTrimmedString(row?.primaryInvestigator),
    phoneNumber: toTrimmedString(row?.phoneNumber),
    notes: toTrimmedString(row?.notes),
    externalId: toTrimmedString(row?.externalId),
    active: parseBooleanValue(row?.active, true),
    enrollmentTarget: row?.enrollmentTarget ?? '',
    enrollmentStartDate: '',
    enrollmentEndDate: '',
    emails: toTrimmedString(row?.emails)
  };

  if (!payload.siteId) {
    errors.push('Site ID is required');
  }
  if (!payload.siteName) {
    errors.push('Site name is required');
  }

  const countryGuid = toTrimmedString(row?.countryGUID) || findCountryGuid(row?.country);
  if (!countryGuid) {
    errors.push('Country not recognized');
  } else {
    payload.countryGUID = countryGuid;
  }

  const timezoneGuid = toTrimmedString(row?.timezoneGUID) || findTimezoneGuid(row?.timezone);
  if (!timezoneGuid) {
    errors.push('Timezone not recognized');
  } else {
    payload.timezoneGUID = timezoneGuid;
  }

  let statusGuid = toTrimmedString(row?.statusGUID) || findStatusGuid(row?.statusLabel);
  if (!statusGuid) {
    statusGuid = defaultStatusGuid;
  }
  if (!statusGuid) {
    errors.push('Unable to determine status');
  } else {
    payload.statusGUID = statusGuid;
  }

  const startDate = normalizeDateValue(row?.enrollmentStartDate);
  if (startDate) {
    payload.enrollmentStartDate = startDate;
  }
  const endDate = normalizeDateValue(row?.enrollmentEndDate);
  if (endDate) {
    payload.enrollmentEndDate = endDate;
  }

  if (!payload.studyGUID) {
    errors.push('No study selected');
  }

  // Normalize emails string to semi-colon separated format
  if (payload.emails) {
    payload.emails = payload.emails
      .split(/[;,]/)
      .map((email) => email.trim())
      .filter(Boolean)
      .join('; ');
  }

  return { payload, errors };
}

function buildImportPayloads(rows) {
  const validRows = [];
  const validationErrors = [];

  rows.forEach((row) => {
    const { payload, errors } = buildSitePayload(row);
    if (errors.length) {
      validationErrors.push({
        row: row.__rowNumber,
        message: errors.join('; ')
      });
    } else {
      validRows.push({
        payload,
        rowNumber: row.__rowNumber
      });
    }
  });

  return { validRows, validationErrors };
}

const REQUIRED_HEADER_LABELS = {
  siteid: 'Site ID',
  sitename: 'Site Name',
  country: 'Country',
  timezone: 'Timezone'
};

async function processSiteExcelFile(file) {
  try {
    const extension = file.name ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase() : '';
    if (!EXCEL_EXTENSIONS.includes(extension)) {
      alert('Please select an Excel file (.xlsx, .xls, .xlsm, .xlsb).');
      return;
    }
    if (!$selectedStudy?._id) {
      alert('Please select a study before importing sites.');
      return;
    }

    showImportModal = true;
    importSummary = null;
    isImportingSites = true;
    importStatusMessage = 'Reading Excel file…';
    importProgress = null;
    await ensureLookupData();
    await fetchAllSites();

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheetName = workbook.SheetNames?.[0];
    if (!sheetName) {
      throw new Error('Excel file has no sheets');
    }
    const worksheet = workbook.Sheets[sheetName];
    const headerRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
    if (!headerRows.length) {
      throw new Error('Excel sheet is empty');
    }
    const normalizedHeaders = (headerRows[0] || []).map((header) => normalizeColumnKey(header));
    const missingHeaders = REQUIRED_SITE_HEADER_KEYS.filter((key) => !normalizedHeaders.includes(key));
    if (missingHeaders.length) {
      const readable = missingHeaders.map((key) => REQUIRED_HEADER_LABELS[key] || key);
      throw new Error(`Missing required column(s): ${readable.join(', ')}`);
    }

    const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
    const mappedRows = rows.map((row, index) => mapExcelRow(row, index)).filter(Boolean);
    if (!mappedRows.length) {
      throw new Error('No data rows found in Excel');
    }
    const totalRows = mappedRows.length;

    importStatusMessage = 'Validating rows…';
    const { validRows, validationErrors } = buildImportPayloads(mappedRows);
    const duplicateErrors = [];
    const existingSiteIds = new Set(
      (allSites || [])
        .map((site) => normalizeComparisonValue(site?.siteId))
        .filter(Boolean)
    );
    const rowsToImport = [];

    validRows.forEach(({ payload, rowNumber }) => {
      const normalizedSiteId = normalizeComparisonValue(payload?.siteId);
      if (!normalizedSiteId) {
        duplicateErrors.push({
          row: rowNumber,
          message: 'Invalid Site ID detected, skipped.'
        });
        return;
      }
      if (existingSiteIds.has(normalizedSiteId)) {
        duplicateErrors.push({
          row: rowNumber,
          message: `Site ID '${payload.siteId}' already exists. Skipped import.`
        });
        return;
      }
      existingSiteIds.add(normalizedSiteId);
      rowsToImport.push({ payload, rowNumber });
    });

    if (!rowsToImport.length) {
      const combined = [...validationErrors, ...duplicateErrors];
      importSummary = {
        total: totalRows,
        success: 0,
        failed: validationErrors.length,
        skipped: duplicateErrors.length,
        errors: combined.slice(0, MAX_IMPORT_ERRORS_TO_DISPLAY),
        errorOverflow: combined.length > MAX_IMPORT_ERRORS_TO_DISPLAY
      };
      return;
    }

    importProgress = {
      total: rowsToImport.length,
      success: 0,
      failed: validationErrors.length + duplicateErrors.length
    };
    const apiErrors = [];

    for (let i = 0; i < rowsToImport.length; i += 1) {
      const { payload, rowNumber } = rowsToImport[i];
      importStatusMessage = `Importing sites… ${i + 1} / ${rowsToImport.length}`;
      try {
        const res = await fetch(`${API_BASE_URL}/sites`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => ({}));
          throw new Error(errorBody?.message || errorBody?.error || 'Failed to create site');
        }
        importProgress.success += 1;
      } catch (error) {
        importProgress.failed += 1;
        apiErrors.push({
          row: rowNumber,
          message: error?.message || 'Failed to create site'
        });
      }
    }

    await fetchSites();
    await fetchAllSites();

    const combinedErrors = [...validationErrors, ...duplicateErrors, ...apiErrors];

    importSummary = {
      total: totalRows,
      success: importProgress.success,
      failed: validationErrors.length + apiErrors.length,
      skipped: duplicateErrors.length,
      errors: combinedErrors.slice(0, MAX_IMPORT_ERRORS_TO_DISPLAY),
      errorOverflow: combinedErrors.length > MAX_IMPORT_ERRORS_TO_DISPLAY
    };
  } catch (error) {
    console.error('processSiteExcelFile error:', error);
    importSummary = {
      total: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      errors: [{ row: null, message: error?.message || 'Failed to import sites' }],
      errorOverflow: false
    };
    showImportModal = true;
  } finally {
    isImportingSites = false;
    importStatusMessage = '';
    importProgress = null;
  }
}

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });
</script>

<PopupForm
  {showForm}
  {editSite}
  on:submit={editSite ? handleEditSite : handleCreateSite}
  on:close={() => {
    showForm = false;
    editSite = null;
  }}
/>

<ConfirmationModal
  {showConfirmation}
  message={`Are you sure you want to delete this site: ${siteToDelete?.siteName || 'Unknown'}?`}
  on:confirm={confirmDelete}
  on:close={() => (showConfirmation = false)}
/>

<AuditLogModal
  showModal={showAuditLog}
  documentId={auditLogSite?._id}
  collection="Site"
  title={`Audit Logs - ${auditLogSite?.siteName || 'Site'}`}
  on:close={() => (showAuditLog = false)}
/>

<StudyForm
  showForm={showStudyForm}
  {editStudy}
  bind:submitError={studyFormError}
  on:submit={editStudy ? handleEditStudy : null}
  on:close={() => {
    showStudyForm = false;
    studyFormError = '';
  }}
/>

<StudyPropertiesEditor
  showForm={showStudyPropertiesEditor}
  on:close={() => (showStudyPropertiesEditor = false)}
/>

<AuditLogModal
  showModal={showStudyAuditLog}
  documentId={auditLogStudy?._id}
  collection="Study"
  title={`Audit Logs - ${auditLogStudy?.studyName || 'Study'}`}
  on:close={() => (showStudyAuditLog = false)}
/>

<StudyHeader />

<FeatureInfo 
  title="Sites Management" 
  description="This page allows you to view and manage all sites for the selected study. You can search, create, edit, and delete sites as needed. The header card shows information about the current study."
  marginBottom="mb-0"
/>

<!-- Debug information - visible in development mode or when debug parameter is set -->
{#if debugMode}
  <div class="bg-yellow-100 text-yellow-800 p-2 text-sm border-l-4 border-yellow-500">
    <div><strong>Debug Info:</strong></div>
    <div>{studyDebugInfo}</div>
    <div>Study from localStorage: {storedStudyFromLocalStorage}</div>
    <div>Time: {new Date().toISOString()}</div>
    <div class="flex gap-2 mt-1">
      <button 
        class="px-2 py-1 bg-blue-500 text-white rounded text-xs"
        on:click={() => selectedStudy.set(JSON.parse(localStorage.getItem('selectedStudy') || 'null'))}
      >
        Reload Study from localStorage
      </button>
      
      <button 
        class="px-2 py-1 bg-green-500 text-white rounded text-xs"
        on:click={testDirectApiCall}
      >
        Test API Variations
      </button>
      
      <button 
        class="px-2 py-1 bg-purple-500 text-white rounded text-xs"
        on:click={fetchSites}
      >
        Refetch Sites
      </button>
      
      <button 
        class="px-2 py-1 bg-orange-500 text-white rounded text-xs"
        on:click={tryAlternativeEndpoint}
      >
        Try Alternative Endpoint
      </button>
    </div>
  </div>
{/if}

{#if $selectedStudy}
  <StudyDetails 
    showOptionsMenu={true}
    {canViewAuditLogs}
    on:edit={(e) => {
      console.log('Edit event received:', e.detail);
      editStudy = e.detail;
      showStudyForm = true;
    }}
    on:configure={(e) => {
      console.log('Configure event received:', e.detail);
      selectedStudy.set(e.detail);
      showStudyPropertiesEditor = true;
    }}
    on:auditLog={(e) => {
      console.log('Audit log event received:', e.detail);
      auditLogStudy = e.detail;
      showStudyAuditLog = true;
    }}
  />
{/if}

{#if isSitesCardViewEnabled}
  <SitesInfoCards sites={allSites} />
{/if}

<Toolbar title="Sites" itemCountTitle="Sites" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="flex items-center gap-2">
      <div class="relative">
        <input
          type="text"
          placeholder="Search"
          bind:value={searchText}
          on:keydown={(e) => e.key === 'Enter' && handleSearch()}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
        />
        {#if searchText}
          <button 
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Clear search"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
      </div>
      <button on:click={handleSearch} title="Search">
        <FontAwesomeIcon icon={faSearch} />
      </button>
    </div>
    <div class="flex items-baseline gap-2">
      Filter:
      <select
        bind:value={participantFilter}
        on:change={handleFilterChange}
        class="h-6 px-2 w-40 border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      >
        <option value="all">All</option>
        <option value="withParticipants">With Participants</option>
      </select>
    </div>
    <div class="relative create-site-menu">
      <button
        on:click={handleCreateMenuToggle}
        class="pagination mx-1 p-0 flex items-center justify-center gap-1"
        disabled={!canAddSites}
        class:opacity-50={!canAddSites}
        title={!canAddSites ? 'Insufficient permissions' : 'Add or import sites'}
      >
        <FontAwesomeIcon icon={faSquarePlus} />
        <FontAwesomeIcon icon={faChevronDown} class="text-[10px]" />
      </button>
      {#if showCreateMenu}
        <div
          class="absolute right-0 mt-2 w-56 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-40 py-1"
        >
          <button
            class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
            on:click={handleCreateMenuFormOption}
            disabled={!canAddSites}
            class:opacity-50={!canAddSites}
          >
            <span>Create site</span>
            <FontAwesomeIcon icon={faSquarePlus} class="text-sky-800 dark:text-white/70" />
          </button>
          <button
            class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
            on:click={handleCreateMenuExcelOption}
            disabled={!canAddSites}
            class:opacity-50={!canAddSites}
          >
            <span>Load from Excel</span>
            <FontAwesomeIcon icon={faFileArrowUp} class="text-sky-800 dark:text-white/70" />
          </button>
          <button
          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
          on:click={() => {
            handleDownloadSiteTemplate();
            showCreateMenu = false;
          }}
        >
          <span>Download template</span>
          <FontAwesomeIcon icon={faFileArrowUp} class="text-sky-800 dark:text-white/70 rotate-180" />
        </button>
        </div>
      {/if}
      <input
        type="file"
        class="hidden"
        bind:this={importFileInput}
        accept={EXCEL_EXTENSIONS.join(',')}
        on:change|preventDefault={handleImportFileChange}
      />
    </div>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if isLoading}
    <div class="flex flex-col items-center justify-center py-12">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-600 dark:text-gray-400">Loading sites...</p>
    </div>
  {:else if sites.length > 0}
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site</th>
          <th class="px-4 py-2 text-left bold">Name</th>
          {#if !$isCompactMode}
            <th class="px-4 py-2 text-left bold">Location</th>
          {/if}
          <th class="px-4 py-2 text-left bold">Country</th>
          <th class="px-4 py-2 text-left bold">Participants</th>
          {#if isFeatureEnabled('performanceMetrics')}
            <th class="px-4 py-2 text-left bold">Enrollment Progress</th>
            <th class="px-4 py-2 text-left bold">Dropout Rate</th>
          {/if}
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="w-8 px-4 py-2 text-left bold">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {#each sites as site, index}
            <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleSiteClick(site)}
              >{site.siteId}</td
            >
            <td class="px-4 py-2 break-words cursor-pointer" on:click={() => handleSiteClick(site)}
              >{site.siteName}</td
            >
            {#if !$isCompactMode}
              <td class="px-4 py-2 cursor-pointer" on:click={() => handleSiteClick(site)}
                >{formatLocation(site)}</td
              >
            {/if}
            <td class="px-4 py-2 min-w-32 cursor-pointer" on:click={() => handleSiteClick(site)}
              >{site.country ? [site.flag ? site.flag + ' ' : ''] + site.country : ''}</td
            >
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleSiteClick(site)}
              >{site.activeSubjectCount === site.subjectCount ? (site.activeSubjectCount ? site.activeSubjectCount : '0') : `${site.activeSubjectCount ? site.activeSubjectCount : '0'} (${site.subjectCount ? site.subjectCount : '0'})`}</td
            >
            {#if isFeatureEnabled('performanceMetrics')}
              <td class="px-4 py-2 min-w-32 cursor-pointer" on:click={() => handleSiteClick(site)}>
                {#if site.enrollmentTarget && site.enrollmentTarget > 0}
                  {@const progress = ((site.activeSubjectCount || 0) / site.enrollmentTarget) * 100}
                  {@const progressText = progress.toFixed(1)}
                  <div class="flex items-center gap-2">
                    <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        class="h-2 rounded-full transition-all duration-300 {progress >= 100 ? 'bg-green-700/80' : progress >= 75 ? 'bg-green-600/60' : progress >= 50 ? 'bg-green-500/40' : 'bg-green-400/20'}"
                        style="width: {Math.min(progress, 100)}%"
                      ></div>
                    </div>
                    <span class="text-xs text-gray-600 dark:text-gray-400 min-w-[3rem]">
                      {progressText}% ({site.enrollmentTarget})
                    </span>
                  </div>
                {:else}
                  <span class="text-xs text-gray-400 dark:text-gray-500">No target set</span>
                {/if}
              </td>
              <td class="px-4 py-2 min-w-32 cursor-pointer" on:click={() => handleSiteClick(site)}>
                {#if site.subjectCount && site.subjectCount > 0}
                  {@const dropoutRate = ((site.discontinuedSubjectCount || 0) / site.subjectCount) * 100}
                  {@const dropoutText = dropoutRate.toFixed(1)}
                  <div class="flex items-center gap-2">
                    <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        class="h-2 rounded-full transition-all duration-300 {dropoutRate >= 20 ? 'bg-red-600/80' : dropoutRate >= 10 ? 'bg-orange-500/60' : dropoutRate >= 5 ? 'bg-yellow-500/40' : 'bg-green-400/20'}"
                        style="width: {Math.min(dropoutRate, 100)}%"
                      ></div>
                    </div>
                    <span class="text-xs text-gray-600 dark:text-gray-400 min-w-[3rem]">
                      {dropoutText}% ({site.discontinuedSubjectCount || 0})
                    </span>
                  </div>
                {:else}
                  <span class="text-xs text-gray-400 dark:text-gray-500">No participants</span>
                {/if}
              </td>
            {/if}
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleSiteClick(site)}>
              <span
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                class:text-green-600={site.statusLabel === 'Active'}
                class:ring-green-600={site.statusLabel === 'Active'}
                class:text-gray-600={site.statusLabel === 'Closed'}
                class:ring-gray-600={site.statusLabel === 'Closed'}
                class:text-sky-600={site.statusLabel === 'Pre-Live'}
                class:ring-sky-600={site.statusLabel === 'Pre-Live'}
              >
                {site.statusLabel}
              </span>
            </td>
            <td class="px-4 py-2 cursor-pointer">
              <div class="menu-container relative">
                <button
                  on:click={(e) => handleMenuClick(site, e)}
                  class="pagination mx-1 p-0"
                  title="More options"
                >
                  <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
                </button>
                {#if showMenu && menuSite?._id === site._id}
                  <div
                    class="fixed w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 menu-dropdown"
                    style="top: {menuPosition.top}px; left: {menuPosition.left}px; max-height: 200px; overflow-y: auto;"
                  >
                    <button
                      on:click={() => {
                        handleEditClick(site);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canEditSites}
                      class:opacity-50={!canEditSites}
                      title={!canEditSites ? 'Insufficient permissions' : 'Edit site'}
                    >
                      <span>Edit site</span>
                      <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => {
                        handleAuditLogClick(site);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canViewAuditLogs}
                      class:opacity-50={!canViewAuditLogs}
                      title={!canViewAuditLogs ? 'Insufficient permissions' : 'View audit logs'}
                    >
                      <span>View audit logs</span>
                      <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => {
                        handleDeleteClick(site);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canDeleteSites}
                      class:opacity-50={!canDeleteSites}
                      title={!canDeleteSites ? 'Insufficient permissions' : 'Delete site'}
                    >
                      <span>Delete site</span>
                      <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                    </button>
                  </div>
                {/if}
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      No sites found
    </div>
  {/if}
</div>

<!-- Pagination and Export Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

{#if showImportModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-stretch gap-4 w-full max-w-md">
      {#if isImportingSites}
        <div class="flex flex-col items-center gap-2 text-center">
          <div class="loader mb-2"></div>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            {importStatusMessage || 'Importing sites...'}
          </p>
          {#if importProgress}
            <div class="text-xs text-gray-600 dark:text-gray-400">
              <div>{importProgress.success} / {importProgress.total} imported</div>
              {#if importProgress.failed}
                <div>{importProgress.failed} issue{importProgress.failed === 1 ? '' : 's'} detected</div>
              {/if}
            </div>
          {/if}
        </div>
      {:else if importSummary}
        <div class="flex items-start justify-between">
          <div>
            <h3 class="font-semibold text-gray-900 dark:text-gray-100 text-base">Import results</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">Processed {importSummary.total} row{importSummary.total === 1 ? '' : 's'}.</p>
          </div>
          <button
            class="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            on:click={closeImportModal}
            aria-label="Close import summary"
          >
            &times;
          </button>
        </div>
        <div class="text-sm text-gray-800 dark:text-gray-200 space-y-1">
          <div>Imported: <span class="font-semibold text-green-600 dark:text-green-400">{importSummary.success}</span></div>
          {#if importSummary.skipped}
            <div>Skipped existing: <span class="font-semibold text-amber-600 dark:text-amber-400">{importSummary.skipped}</span></div>
          {/if}
          {#if importSummary.failed}
            <div>Failed validation: <span class="font-semibold text-red-600 dark:text-red-400">{importSummary.failed}</span></div>
          {/if}
          {#if !importSummary.skipped && !importSummary.failed}
            <div class="text-green-700 dark:text-green-400 text-xs">All rows imported successfully.</div>
          {/if}
        </div>
        {#if importSummary.errors?.length}
          <div class="rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3 max-h-48 overflow-y-auto">
            <p class="text-xs font-semibold text-red-700 dark:text-red-300 mb-1">Details</p>
            <ul class="space-y-1 text-xs text-red-700 dark:text-red-200">
              {#each importSummary.errors as issue}
                <li>{issue.row ? `Row ${issue.row}: ` : ''}{issue.message}</li>
              {/each}
            </ul>
            {#if importSummary.errorOverflow}
              <p class="text-[11px] text-red-600 dark:text-red-300 mt-1">
                Showing first {MAX_IMPORT_ERRORS_TO_DISPLAY} issue{MAX_IMPORT_ERRORS_TO_DISPLAY === 1 ? '' : 's'}.
              </p>
            {/if}
          </div>
        {/if}
        <div class="flex justify-end">
          <button
            class="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-full"
            on:click={closeImportModal}
          >
            Close
          </button>
        </div>
      {:else}
        <div class="flex flex-col items-center gap-3 text-center">
          <p class="text-sm text-gray-700 dark:text-gray-300">No import details available.</p>
          <button
            class="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-full"
            on:click={closeImportModal}
          >
            Close
          </button>
        </div>
      {/if}
    </div>
  </div>
{/if}

{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<style>
.menu-dropdown {
  transition: none;
}
.loader {
  border: 2px solid #20669c;
  border-radius: 50%;
  border-top: 4px solid #98acb4;
  width: 32px;
  height: 32px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.fixed {
  position: fixed;
}

.inset-0 {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.bg-opacity-50 {
  --tw-bg-opacity: 0.5;
}

.z-50 {
  z-index: 50;
}

.shadow-lg {
  --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
    0 4px 6px -4px var(--tw-shadow-color);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
    var(--tw-shadow);
}
</style>
